import axios from "axios";

/**
 * Strapi client for playlists. Reads directly from a self-hosted Strapi instance
 * using a read-only API token (like the Ghost blog client). Configure via env:
 *   REACT_APP_STRAPI_URL    e.g. https://cms.blindnightrecords.com
 *   REACT_APP_STRAPI_TOKEN  a read-only API token from Strapi admin
 */

const STRAPI_URL = (process.env.REACT_APP_STRAPI_URL ?? "http://localhost:1337").replace(/\/$/, "");
const STRAPI_TOKEN = process.env.REACT_APP_STRAPI_TOKEN ?? "";

export interface PlatformLink {
  platform: string;
  url: string;
}

// What the app consumes, mapped from Strapi's response shape.
export interface Playlist {
  id: number;
  slug: string;
  title: string;
  description: string;
  coverImageUrl: string | null;
  featured: boolean;
  links: PlatformLink[];
}

// --- Strapi v4 response types (subset we use) ---
interface StrapiMedia {
  data: { attributes: { url: string } } | null;
}
interface StrapiPlaylistAttributes {
  title: string;
  slug: string;
  description: string | null;
  featured: boolean | null;
  sortOrder: number | null;
  coverImage: StrapiMedia;
  links: PlatformLink[] | null;
}
interface StrapiEntry<T> {
  id: number;
  attributes: T;
}
interface StrapiListResponse<T> {
  data: StrapiEntry<T>[];
}

const api = axios.create({
  baseURL: `${STRAPI_URL}/api`,
  headers: STRAPI_TOKEN ? { Authorization: `Bearer ${STRAPI_TOKEN}` } : undefined,
});

// Strapi returns media urls relative to its host; make them absolute.
const absoluteUrl = (url: string | undefined): string | null =>
  !url ? null : url.startsWith("http") ? url : `${STRAPI_URL}${url}`;

const mapPlaylist = (entry: StrapiEntry<StrapiPlaylistAttributes>): Playlist => {
  const a = entry.attributes;
  return {
    id: entry.id,
    slug: a.slug,
    title: a.title,
    description: a.description ?? "",
    coverImageUrl: absoluteUrl(a.coverImage?.data?.attributes?.url),
    featured: a.featured ?? false,
    links: a.links ?? [],
  };
};

// Fetch all published playlists, ordered by sortOrder then title.
export const getPlaylists = async (): Promise<Playlist[]> => {
  const response = await api.get<StrapiListResponse<StrapiPlaylistAttributes>>("/playlists", {
    params: {
      populate: "*",
      "sort[0]": "sortOrder:asc",
      "sort[1]": "title:asc",
      "pagination[pageSize]": 100,
    },
  });
  return response.data.data.map(mapPlaylist);
};
